/**
 * Copy Service — copies selected content fields from one audience's day
 * to another audience's day, with configurable conflict handling.
 *
 * Conflict strategies:
 *   empty_only — only writes fields that are currently null/empty in the target
 *   replace    — overwrites target fields regardless of existing content
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getDayInfo, dayNumberToDocId } from '@/config/pregnancyCalendar';
import { auditService } from '@/services/auditService';
import type { Audience, UserRef } from '@/types/auth.types';
import type { DailyContent, ContentFieldKey, CopyConflictStrategy } from '@/types/content.types';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CopyResult {
  copied:  ContentFieldKey[];   // fields actually written
  skipped: ContentFieldKey[];   // fields skipped (conflict / empty source)
}

// ─── Helpers ───────────────────────────────────────────────────name────────────

function dayDocRef(audience: Audience, dayNumber: number) {
  return doc(db, 'pregnancyContent', audience, 'days', dayNumberToDocId(dayNumber));
}

function fieldValue(content: DailyContent | null, field: ContentFieldKey): string | null {
  if (!content) return null;
  return (content[field] as string | null) ?? null;
}

function isEmpty(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === '';
}

// ─── Core copy function ────────────────────────────────────────────────────────

export async function copyDayContent(
  sourceAudience:   Audience,
  targetAudience:   Audience,
  dayNumber:        number,
  fields:           ContentFieldKey[],
  conflictStrategy: CopyConflictStrategy,
  userRef:          UserRef,
): Promise<CopyResult> {
  if (sourceAudience === targetAudience) {
    throw new Error('Source and target audience must be different.');
  }
  if (fields.length === 0) {
    throw new Error('Select at least one field to copy.');
  }

  const info = getDayInfo(dayNumber);
  if (!info) throw new Error(`Invalid day number: ${dayNumber}`);

  const docId = dayNumberToDocId(dayNumber);

  // Load source + target concurrently
  const [sourceSnap, targetSnap] = await Promise.all([
    getDoc(dayDocRef(sourceAudience, dayNumber)),
    getDoc(dayDocRef(targetAudience, dayNumber)),
  ]);

  const source = sourceSnap.exists()
    ? (sourceSnap.data() as DailyContent)
    : null;

  const target = targetSnap.exists()
    ? (targetSnap.data() as DailyContent)
    : null;

  const copied:  ContentFieldKey[] = [];
  const skipped: ContentFieldKey[] = [];
  const payload: Record<string, unknown> = {};

  for (const field of fields) {
    const srcValue = fieldValue(source, field);

    // Skip if source field is empty — nothing to copy
    if (isEmpty(srcValue)) {
      skipped.push(field);
      continue;
    }

    const tgtValue = fieldValue(target, field);

    if (conflictStrategy === 'empty_only' && !isEmpty(tgtValue)) {
      // Target already has content and strategy says don't overwrite
      skipped.push(field);
      continue;
    }

    payload[field] = srcValue;
    copied.push(field);
  }

  if (copied.length === 0) {
    return { copied, skipped };
  }

  const now = serverTimestamp();

  // Write to target doc (merge so we don't clobber other fields)
  await setDoc(
    dayDocRef(targetAudience, dayNumber),
    {
      // Identity fields — ensure doc is self-describing
      audience:    targetAudience,
      dayNumber,
      weekNumber:  info.weekNumber,
      monthNumber: info.monthNumber,

      // Copied content
      ...payload,

      // Audit metadata
      updatedBy: userRef,
      updatedAt: now,
      // Set createdAt only if the doc didn't exist yet
      ...(target === null ? { createdBy: userRef, createdAt: now } : {}),
    },
    { merge: true },
  );

  // Write audit log
  await auditService.log(userRef, 'content_copied', {
    audience:    targetAudience,
    dayNumber,
    weekNumber:  info.weekNumber,
    monthNumber: info.monthNumber,
    recordId:    docId,
    fieldsChanged: copied,
    metadata: {
      sourceAudience,
      targetAudience,
      conflictStrategy,
      fieldsCopied:  copied,
      fieldsSkipped: skipped,
    },
  });

  return { copied, skipped };
}

// ─── Preview helper (pure — no Firestore calls) ────────────────────────────────

export interface FieldCopyPreview {
  field:      ContentFieldKey;
  hasSource:  boolean;
  hasTarget:  boolean;
  willCopy:   boolean;
  reason:     'copied' | 'skipped_empty_source' | 'skipped_conflict' | 'not_selected';
}

export function previewCopy(
  source:           DailyContent | null,
  target:           DailyContent | null,
  fields:           ContentFieldKey[],
  allFields:        ContentFieldKey[],
  conflictStrategy: CopyConflictStrategy,
): FieldCopyPreview[] {
  return allFields.map(field => {
    const srcValue = fieldValue(source, field);
    const tgtValue = fieldValue(target, field);
    const hasSource = !isEmpty(srcValue);
    const hasTarget = !isEmpty(tgtValue);
    const selected  = fields.includes(field);

    if (!selected) {
      return { field, hasSource, hasTarget, willCopy: false, reason: 'not_selected' };
    }
    if (!hasSource) {
      return { field, hasSource, hasTarget, willCopy: false, reason: 'skipped_empty_source' };
    }
    if (conflictStrategy === 'empty_only' && hasTarget) {
      return { field, hasSource, hasTarget, willCopy: false, reason: 'skipped_conflict' };
    }
    return { field, hasSource, hasTarget, willCopy: true, reason: 'copied' };
  });
}
